import { UserCard } from "@/components/user-card/UserCard.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { useTeamSearch } from "@/queries/use-teams.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiTeamFilter } from "@/types/team.types.ts";
import {
  Avatar,
  Box,
  Button,
  EmptyStateBase,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const defaultVisibleTeamsCount = 8;

export function EmployeePageOverviewTeams({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { canIView, canIEdit } = usePermissions();
  const navigate = useNavigate();
  const stripHtmlTags = (html?: string) => html?.replace(/<[^>]*>/g, "");
  const [isShowAllTeams, setIsShowAllTeams] = useState(false);

  const [teamFilter, setTeamFilter] = useState<ApiTeamFilter>({
    page_number: 1,
    employee_ids: [employee.id],
  });

  const [syncedEmployeeId, setSyncedEmployeeId] = useState(employee.id);

  if (employee.id !== syncedEmployeeId) {
    setSyncedEmployeeId(employee.id);
    setTeamFilter((prevFilter) => ({
      ...prevFilter,
      employee_ids: [employee.id],
      page_number: 1,
    }));
    setIsShowAllTeams(false);
  }

  const canSeeTeams =
    canIView(ApiPermission.manage_teams) && canIEdit(ApiPermission.see_employee_directory);
  const canManageTeams = canIEdit(ApiPermission.manage_teams);

  const { data: teams, isLoading: teamsIsLoading } = useTeamSearch(teamFilter, canSeeTeams);
  const teamsCount = teams?.metadata.total_items ?? 0;
  const visibleTeams = isShowAllTeams
    ? teams?.items
    : teams?.items?.slice(0, defaultVisibleTeamsCount);
  const remainingTeamsCount = Math.max(teamsCount - defaultVisibleTeamsCount, 0);
  const isEmpty = !teamsIsLoading && teamsCount === 0;
  const shouldShowMoreButton =
    !teamsIsLoading && !isEmpty && !isShowAllTeams && remainingTeamsCount > 0;
  const titleCaption = teamsIsLoading
    ? "..."
    : isEmpty
      ? undefined
      : t("plurals:teams_count", { count: teamsCount });

  if (!canSeeTeams) return null;

  const renderOverviewTeams = () => {
    return (
      <>
        <Stack direction={"vertical"} gap={"md"}>
          <Title size={"lg"} header={t("core:teams")} caption={titleCaption} />
          {teamsIsLoading ? (
            <Stack direction={"horizontal"} gap={"md"}>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
            </Stack>
          ) : isEmpty ? (
            <EmptyStateBase
              variant="primary"
              title={t("core:employee_overview_no_team_membership")}
              description={
                canManageTeams
                  ? t("core:employee_overview_no_team_membership_description")
                  : undefined
              }
              emoji={":rocket:"}
              primaryButton={
                canManageTeams ? (
                  <Button
                    onClick={() => {
                      navigate("/employees/teams/");
                    }}
                  >
                    {t("core:employee_overview_no_team_membership_button")}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <Box
                className={"grid gap-(--gap-md)"}
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))" }}
              >
                {visibleTeams?.map((team, index) => {
                  return (
                    <UserCard
                      to={`/employees/teams/${team.id}`}
                      key={`team-card-link-${index}-${team.id}`}
                      size={"md"}
                      className={"w-full"}
                      textEllipsis={true}
                      avatarElement={<Avatar variant={"default"} emoji={team.emoji} size={"2xl"} />}
                      title={team.title}
                      captionSlot={
                        <Stack className={"text-base text-dark/60 px-2 whitespace-nowrap"}>
                          <Text className={"overflow-hidden text-ellipsis"}>
                            {stripHtmlTags(team.description)}
                          </Text>
                          <Text>{`${team.members.length} ${t("core:people")}`}</Text>
                        </Stack>
                      }
                    />
                  );
                })}
              </Box>

              {shouldShowMoreButton && (
                <Stack className="pt-(--gap-md)" direction="horizontal">
                  <Button variant="secondary" onClick={() => setIsShowAllTeams(true)}>
                    {t("common:show_more_template", { count: remainingTeamsCount })}
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </>
    );
  };

  return (
    <>
      <div className={"flex flex-row gap-9 py-0"}>
        <div className={"w-full overflow-hidden"}>{renderOverviewTeams()}</div>
      </div>
    </>
  );
}
